import { PageHeader } from '@/shared/components/PageHeader';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/shared/ui/accordion';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import {
  Activity,
  BookOpen,
  Clock,
  Code,
  ExternalLink,
  FileText,
  HelpCircle,
  History,
  Mail,
  Search,
  Send,
  Video,
} from 'lucide-react';
import { useState } from 'react';

type Category = 'all' | 'jobs' | 'users' | 'teams' | 'planner' | 'reports';

interface QuickLink {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  href: string;
  external?: boolean;
}

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: Category;
}

const QUICK_LINKS: QuickLink[] = [
  {
    id: 'getting-started',
    title: 'Getting Started Guide',
    description: 'Learn the basics of Skewb Works Lite',
    icon: BookOpen,
    href: '/docs/getting-started',
    external: false,
  },
  {
    id: 'video-tutorials',
    title: 'Video Tutorials',
    description: 'Watch step-by-step video guides',
    icon: Video,
    href: '/docs/videos',
    external: false,
  },
  {
    id: 'user-docs',
    title: 'User Documentation',
    description: 'Complete user manual and guides',
    icon: FileText,
    href: '/docs',
    external: false,
  },
  {
    id: 'api-docs',
    title: 'API Documentation',
    description: 'Developer API reference',
    icon: Code,
    href: '/api/docs',
    external: false,
  },
  {
    id: 'release-notes',
    title: 'Release Notes',
    description: "What's new in the latest version",
    icon: History,
    href: '/docs/releases',
    external: false,
  },
  {
    id: 'system-status',
    title: 'System Status',
    description: 'Check system health and uptime',
    icon: Activity,
    href: 'https://status.skewbdigital.co.uk',
    external: true,
  },
];

const FAQ_ITEMS: FAQItem[] = [
  {
    id: 'create-job',
    question: 'How do I create a new job?',
    answer:
      'To create a new job, navigate to the Jobs page and click the "New Job" button in the top right. Fill in the required information including job title, description, site address, and assignment details. You can also assign the job to a team member and set priority levels. Once complete, click "Create Job" to save.',
    category: 'jobs',
  },
  {
    id: 'manage-users',
    question: 'How do I manage users?',
    answer:
      'User management is available in the Users section. From there, you can add new users by clicking "Add User", edit existing user details, manage roles and permissions, and view user activity. You can also assign users to teams and track their completed jobs.',
    category: 'users',
  },
  {
    id: 'schedule-planner',
    question: 'How do I schedule work on the planner?',
    answer:
      'The Planner page provides a calendar view where you can drag and drop jobs to schedule them. Click on any date to create a new scheduled item, or click and drag existing jobs to reschedule them. You can filter the view by team member, team, or job type. Colour coding helps identify different job types and priorities.',
    category: 'planner',
  },
  {
    id: 'view-reports',
    question: 'How do I view reports?',
    answer:
      'Reports are available on the Dashboard page. Here you can view key metrics including total jobs, completion rates, team performance, and job value trends. Use the filters to customise the date range and other parameters. Charts and graphs provide visual insights into operational performance.',
    category: 'reports',
  },
  {
    id: 'export-data',
    question: 'How do I export data?',
    answer:
      'Data can be exported from most pages using the "Export" or "Print" buttons. On the Jobs page, you can export the current filtered view to CSV or PDF. The Dashboard allows you to export charts and reports. For bulk data exports, use the Settings page where you can schedule automated exports.',
    category: 'reports',
  },
  {
    id: 'job-status',
    question: 'What do the different job statuses mean?',
    answer:
      'Jobs have four status levels: "Not Started" (pending work), "In Progress" (actively being worked on), "Completed" (finished and verified), and "Urgent" (requires immediate attention). You can update job status from the Jobs page or within the job detail view.',
    category: 'jobs',
  },
  {
    id: 'team-capacity',
    question: 'How do I check team capacity?',
    answer:
      'Team capacity and utilisation metrics are visible on the Dashboard and Teams pages. Each team shows current utilisation percentage, active jobs, and completed jobs. Use this information to balance workload when assigning new jobs.',
    category: 'teams',
  },
  {
    id: 'notifications',
    question: 'How do I manage notifications?',
    answer:
      'Notification settings can be configured in your user profile. You can choose to receive notifications for job assignments, status changes, approaching deadlines, and team updates. Notifications can be delivered via email, in-app alerts, or both.',
    category: 'users',
  },
  {
    id: 'mobile-access',
    question: 'Can I access the system from mobile devices?',
    answer:
      'Yes, Skewb Works Lite is fully responsive and works on mobile devices. Field workers can access their assigned jobs, update job status, add notes, and upload photos directly from their mobile devices. A dedicated mobile app is also available for iOS and Android.',
    category: 'all',
  },
];

const CATEGORY_LABELS: Record<Category, string> = {
  all: 'All Topics',
  jobs: 'Jobs',
  users: 'Users',
  teams: 'Teams',
  planner: 'Planner',
  reports: 'Reports',
};

export default function HelpPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<Category>('all');
  const [supportForm, setSupportForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Filter FAQ items
  const filteredFAQs = FAQ_ITEMS.filter((faq) => {
    const matchesSearch =
      searchTerm === '' ||
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory =
      selectedCategory === 'all' || faq.category === selectedCategory || faq.category === 'all';

    return matchesSearch && matchesCategory;
  });

  const handleSupportSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would typically send the form data to your backend
    console.log('Support form submitted:', supportForm);
    setFormSubmitted(true);
    // Reset form after 3 seconds
    setTimeout(() => {
      setSupportForm({ name: '', email: '', subject: '', message: '' });
      setFormSubmitted(false);
    }, 3000);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <PageHeader
        title="Help & Support"
        subtitle="Find answers, resources, and contact support"
        icon={<HelpCircle className="w-8 h-8" style={{ color: '#E84A2B' }} strokeWidth={1.5} />}
      />

      <div className="flex-1 overflow-y-auto px-6 space-y-6 pb-8">
        {/* Search Bar */}
        <div className="bg-card border border-border rounded-lg shadow-sm p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search help topics..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-background border-border"
            />
          </div>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2">
          {(Object.keys(CATEGORY_LABELS) as Category[]).map((category) => (
            <Button
              key={category}
              variant={selectedCategory === category ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(category)}
              className={`rounded-lg ${
                selectedCategory === category
                  ? 'bg-orange-600 hover:bg-orange-700 text-white'
                  : 'border-border hover:bg-accent'
              }`}
            >
              {CATEGORY_LABELS[category]}
            </Button>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* FAQ Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">Frequently Asked Questions</h2>
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                {filteredFAQs.length} {filteredFAQs.length === 1 ? 'topic' : 'topics'}
              </Badge>
            </div>

            <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
              {filteredFAQs.length > 0 ? (
                <Accordion type="single" collapsible className="w-full">
                  {filteredFAQs.map((faq) => (
                    <AccordionItem key={faq.id} value={faq.id} className="px-5">
                      <AccordionTrigger className="text-left hover:no-underline">
                        <span className="font-medium text-foreground">{faq.question}</span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {faq.answer}
                        </p>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                  <div className="text-center">
                    <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No help topics found</p>
                    <p className="text-xs mt-1">Try a different search or category</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Links Section */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-foreground">Quick Links</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {QUICK_LINKS.map((link) => {
                const Icon = link.icon;
                return (
                  <a
                    key={link.id}
                    href={link.href}
                    target={link.external ? '_blank' : undefined}
                    rel={link.external ? 'noopener noreferrer' : undefined}
                    className="bg-card border border-border rounded-lg p-4 hover:border-orange-300 hover:shadow-md transition-all group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0 group-hover:bg-orange-100 transition-colors">
                        <Icon className="w-5 h-5 text-orange-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-foreground text-sm group-hover:text-orange-600 transition-colors">
                            {link.title}
                          </h3>
                          {link.external && (
                            <ExternalLink className="w-3 h-3 text-muted-foreground" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {link.description}
                        </p>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Contact Support Section */}
        <div className="bg-card border border-border rounded-lg shadow-sm p-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
              <Mail className="w-6 h-6 text-orange-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-foreground mb-1">Contact Support</h2>
              <p className="text-sm text-muted-foreground">
                Can't find what you're looking for? Our support team is here to help.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Support Form */}
            <form onSubmit={handleSupportSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  placeholder="Your name"
                  value={supportForm.name}
                  onChange={(e) => setSupportForm({ ...supportForm, name: e.target.value })}
                  required
                  className="border-border"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={supportForm.email}
                  onChange={(e) => setSupportForm({ ...supportForm, email: e.target.value })}
                  required
                  className="border-border"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  placeholder="Brief description of your issue"
                  value={supportForm.subject}
                  onChange={(e) => setSupportForm({ ...supportForm, subject: e.target.value })}
                  required
                  className="border-border"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  placeholder="Describe your issue or question in detail..."
                  value={supportForm.message}
                  onChange={(e) => setSupportForm({ ...supportForm, message: e.target.value })}
                  required
                  className="border-border min-h-[120px]"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                disabled={formSubmitted}
              >
                {formSubmitted ? (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Message Sent!
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>

            {/* Support Info */}
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-semibold text-blue-900 mb-1">Email Support</h3>
                    <a
                      href="mailto:support@skewbdigital.co.uk"
                      className="text-sm text-blue-700 hover:underline"
                    >
                      support@skewbdigital.co.uk
                    </a>
                    <p className="text-xs text-blue-600 mt-1">
                      We typically respond within 24 hours
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-semibold text-green-900 mb-1">Support Hours</h3>
                    <p className="text-sm text-green-700">Monday - Friday</p>
                    <p className="text-sm text-green-700">9:00 AM - 5:00 PM GMT</p>
                    <p className="text-xs text-green-600 mt-1">Emergency support available 24/7</p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-900 mb-2">Before you contact us</h3>
                <ul className="space-y-1.5 text-xs text-slate-600">
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 mt-0.5">•</span>
                    <span>Check the FAQ section above for quick answers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 mt-0.5">•</span>
                    <span>Search our documentation for detailed guides</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 mt-0.5">•</span>
                    <span>Include relevant details like job IDs or error messages</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 mt-0.5">•</span>
                    <span>Attach screenshots if applicable</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
